/**
 * useMetaEmbeddedSignup — launches Meta's WhatsApp Embedded Signup popup.
 *
 * Flow: the Facebook JS SDK opens Meta's own onboarding (business → WhatsApp
 * Business Account → phone number, including "connect your WhatsApp Business
 * app number" when Meta offers coexistence). Two things come back:
 *   1. a `message` event carrying the chosen waba_id / phone_number_id,
 *   2. an OAuth `code` from FB.login.
 * Both are posted to the backend, which exchanges the code with the app secret
 * and stores the connection — the browser never sees a long-lived token.
 *
 * App id and configuration id are fetched from the server (GET .../config), so
 * nothing Meta-specific is baked into the bundle.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import APICall from '../../APICalls/APICall';

const BASE = '/channels/whatsapp/embedded-signup';
const SDK_ID = 'facebook-jssdk';

// Meta's popup reports progress via window.postMessage from facebook.com.
const META_ORIGINS = ['https://www.facebook.com', 'https://web.facebook.com', 'https://business.facebook.com'];

let sdkPromise = null;   // module-level: the SDK is loaded and initialised once

function loadSdk(appId, graphVersion) {
  if (sdkPromise) return sdkPromise;
  sdkPromise = new Promise((resolve, reject) => {
    if (window.FB) { resolve(window.FB); return; }

    window.fbAsyncInit = function () {
      try {
        window.FB.init({ appId, autoLogAppEvents: true, xfbml: false, version: graphVersion });
        resolve(window.FB);
      } catch (err) {
        reject(err);
      }
    };

    const existing = document.getElementById(SDK_ID);
    if (existing) return;                       // script already injected; fbAsyncInit will fire
    const js = document.createElement('script');
    js.id = SDK_ID;
    js.async = true;
    js.defer = true;
    js.crossOrigin = 'anonymous';
    js.src = 'https://connect.facebook.net/en_US/sdk.js';
    js.onerror = () => {
      sdkPromise = null;                        // allow a retry after a network failure
      reject(new Error('Could not load the Facebook SDK. Check your network or ad blocker.'));
    };
    document.body.appendChild(js);
  });
  return sdkPromise;
}

// Meta's `extras.featureType`: omitted for the standard Cloud API onboarding,
// "whatsapp_business_app_onboarding" to offer numbers that stay in the WhatsApp
// Business app (coexistence). Anything else makes the dialog fall back to a
// plain login, so only these two forms are used.
export const FEATURE_COEXISTENCE = 'whatsapp_business_app_onboarding';

export default function useMetaEmbeddedSignup({ onConnected, featureType } = {}) {
  const [config, setConfig]   = useState(null);   // {enabled, app_id, config_id, graph_version}
  const [loading, setLoading] = useState(true);
  const [busy, setBusy]       = useState(false);
  const [status, setStatus]   = useState(null);   // {variant, msg}
  // Meta's popup posts the ids before FB.login resolves, so keep them aside.
  const signupData = useRef({});

  const reloadConfig = useCallback(() => {
    setLoading(true);
    return APICall.getT(`${BASE}/config`)
      .then(data => setConfig(data))
      .catch(() => setConfig({ enabled: false, missing: [] }))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { reloadConfig(); }, [reloadConfig]);

  useEffect(() => {
    const onMessage = (event) => {
      if (!META_ORIGINS.includes(event.origin)) return;
      let payload = event.data;
      if (typeof payload === 'string') {
        try { payload = JSON.parse(payload); } catch { return; }
      }
      if (!payload || payload.type !== 'WA_EMBEDDED_SIGNUP') return;

      // Meta's shape: { type, event, version, data: { waba_id, phone_number_id,
      // current_step, error_message } } — the event name sits NEXT TO data, not
      // inside it. Older/newer builds have been seen putting it in both places,
      // so accept either rather than silently losing the ids.
      const d = payload.data || {};
      const waEvent = payload.event || d.event;

      if (waEvent && String(waEvent).startsWith('FINISH')) {
        signupData.current = {
          waba_id: d.waba_id || payload.waba_id,
          phone_number_id: d.phone_number_id || payload.phone_number_id,
          // Meta uses a dedicated finish event for WhatsApp Business app numbers.
          connection_type: waEvent === 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING' ? 'coexistence' : 'cloud_api',
        };
      } else if (waEvent === 'CANCEL') {
        signupData.current = {};
        setStatus({
          variant: 'warning',
          msg: d.current_step
            ? `Setup was cancelled at the "${d.current_step}" step.`
            : 'Setup was cancelled before it finished.',
        });
      } else if (waEvent === 'ERROR') {
        signupData.current = {};
        setStatus({ variant: 'danger', msg: d.error_message || 'Meta reported an error during setup.' });
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  const connect = useCallback(async () => {
    if (!config?.enabled) {
      const missing = (config?.missing || []).join(', ');
      setStatus({
        variant: 'danger',
        msg: missing
          ? `Embedded Signup is not configured yet (missing: ${missing}).`
          : 'Embedded Signup is switched off for this server.',
      });
      return;
    }
    setBusy(true);
    setStatus(null);
    signupData.current = {};

    try {
      const FB = await loadSdk(config.app_id, config.graph_version);

      const extras = { setup: {}, sessionInfoVersion: '3' };
      if (featureType) extras.featureType = featureType;

      const authResponse = await new Promise((resolve) => {
        FB.login(resolve, {
          config_id: config.config_id,
          response_type: 'code',            // server-side exchange; no token in the browser
          override_default_response_type: true,
          extras,
        });
      });

      const code = authResponse?.authResponse?.code;
      if (!code) {
        // FB.login resolves with no code when the window was closed or login failed.
        setStatus(prev => prev || {
          variant: 'warning',
          msg: 'Setup was closed before Meta granted access. Please try again and finish every step.',
        });
        return;
      }

      // The ids are a convenience: when Meta's popup doesn't post them (it
      // varies by onboarding path), the server derives them from the token it
      // exchanges, so the connection still completes.
      const { waba_id, phone_number_id, connection_type } = signupData.current;

      setStatus({ variant: 'info', msg: 'Finishing the connection with Meta…' });
      // An Embedded Signup code is exchanged with client_id + client_secret +
      // code alone — no redirect_uri is involved anywhere in this flow.
      const res = await APICall.postT(`${BASE}/exchange`, {
        code, waba_id, phone_number_id, connection_type,
      });
      const c = res?.connection || {};
      setStatus({
        variant: 'success',
        msg: `Connected ${c.display_phone_number || c.phone_number_id || 'number'}`
          + (c.connection_type === 'coexistence' ? ' (WhatsApp Business app coexistence).' : '.'),
      });
      onConnected?.(res);
    } catch (err) {
      setStatus({ variant: 'danger', msg: err?.message || 'WhatsApp connection failed.' });
    } finally {
      setBusy(false);
    }
  }, [config, featureType, onConnected]);

  return { config, loading, busy, status, setStatus, connect, reloadConfig };
}
